/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  CertSchemaField,
  CreateApiParam,
  fieldKeyCodec,
  getInitialFormData,
  SignatureGroupType,
} from '@stormwater-software/lib-certification';
import { FormContextProvider } from '@stormwater-software/lib-form';
import { get, set } from 'lodash';
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { offerDownloadRemoval } from '../components/Shared/OfflineAutoSync/RemoveDownloadPrompt';
import LoadingModal from '../components/Shared/Loading/LoadingModal';
import { Loading } from '../components/Shared/ResourceIndex/Loading';
import SuccessNotification from '../components/Shared/SuccessNotification/SuccessNotification';
import {
  certificationApi,
  CertificationCreateResponse,
  CertificationIndexResponseWithReview,
} from '../utils/api/certification';
import {
  CertSignatureGroup,
  getCertLineDefaultDate,
  resolveProjectTimezone,
} from '../utils/certDefaultDate';
import {
  getRequiredFindingCompletion,
  selectRequiredForCertFindings,
} from '../utils/certRequiredFindings';
import { queuedSignatureMessage, reviewHoldsReport } from '../utils/offlineSigning';
import AppContext from './AppContext';
import DashboardContext from './DashboardContext';
import { InspectionContext } from './InspectionContext';
import NotificationStore from '../contexts/NotificationsContext';
import { ProjectContext } from '../contexts/ProjectContext';

type CertificationContextValue = {
  // loading: boolean;
  error?: string;
  state: CertificationIndexResponseWithReview;
  onSave: SaveHandler;
  // setShowModal: Dispatch<ShowModalState>;
  flashSuccess: () => void;
  reload: () => void;
};

type ShowModalState = null | 'loading' | 'success';
type SaveHandler = (
  arg: Pick<CreateApiParam, 'lineType' | 'group'> & {
    formData: any;
  },
  // SW2-2459: false when the signature was not saved (already reported to the user).
) => Promise<boolean | void>;

const InspectionCertificationContext =
  React.createContext<CertificationContextValue>({
    // loading: true,
    onSave: async () => { },
    flashSuccess: () => { },
    state: {} as CertificationIndexResponseWithReview,
    reload: () => { },
  });

export const InspectionCertificationContextProvider: React.FC<
  PropsWithChildren<{}>
> = ({ children }) => {
  const inspectionContext = useContext(InspectionContext);
  const projectContext = useContext(ProjectContext);
  const { notificationSettings } = useContext(NotificationStore)
  const { inspectionsCertDue, fetchStatsFuncForID } = useContext(DashboardContext)
  const appContext = useContext(AppContext);
  const { id: inspectionId } = inspectionContext.inspection;
  const query = useQuery({
    queryKey: ['cert', inspectionId],
    queryFn: () => certificationApi.index(inspectionId),
    refetchOnMount: false,
    refetchOnWindowFocus: true,
  });
  const [showModal, setShowModal] = useState<ShowModalState>(null);

  /**
   * SW2-2239. The five hand-rolled date functions that used to live here — `isFindingRequired`,
   * `latestFindingClosureDate`, `inspectorDefaultDate`, `complianceDefaultDate` and
   * `acknowledgementDefaultDate` — are replaced by the shared resolver in `utils/certDefaultDate`.
   *
   * Three things were wrong with them:
   *
   *  1. They read `signatureGroups.<group>[0].defaultDateLogic` and applied the FIRST line's setting to
   *     every line in the group, but the column is per line.
   *  2. `complianceDefaultDate` checked the finding closure ABOVE the `defaultDateLogic` gate, so a line
   *     configured "Current Date" still got the closure date. The bulk modals nested it correctly.
   *  3. `latestFindingClosureDate` compared a UTC-midnight DOI against local-midnight completion dates.
   *     For a finding completed before the DOI that returned the UTC-midnight value, which renders as
   *     the day BEFORE in any US timezone and correctly in IST — invisible to a developer in India.
   */
  const timezone = resolveProjectTimezone(
    (query.data as any)?.projectTimezone,
    projectContext?.project?.timezone,
  );

  const requiredFindingCompletion = getRequiredFindingCompletion(
    selectRequiredForCertFindings(inspectionContext.findings),
    timezone,
  );
  /** Primitive memo dependency — see the note on `initialFormData` below. */
  const requiredCompletionMs = requiredFindingCompletion?.getTime() ?? null;

  const initialFormData = useMemo(() => {
    if (query.data) {
      const { default_email_message: emailMessage = '' } =
        appContext.getNotificationSetting('Inspection Complete') || {};

      /**
       * `getInitialFormData` takes ONE date per group, which cannot express per-line logic, so the
       * per-group dates are no longer passed and each line's date is applied afterwards.
       *
       * The two guards below are what make that safe without re-implementing `getDefaults`' branching:
       * it emits the date key only for a signed line (value = the signature's own date) or an enabled
       * unsigned one, and nothing at all for a disabled group. `key in initialData && !signed` therefore
       * selects exactly the lines it defaulted, leaving `emailMessage` and the internal `Object.assign`
       * ordering untouched.
       */
      const initialData = getInitialFormData({
        ...query.data,
        user: {
          ...appContext.get('user'),
          emailMessage,
        },
      });

      Object.entries(query.data.signatureGroups ?? {}).forEach(
        ([group, lines]) => {
          (lines as any[])?.forEach((line: any) => {
            const key = fieldKeyCodec.encode(line.type, CertSchemaField.date);
            const signed = (query.data as any)?.userSignatures?.[
              group
            ]?.some((sig: any) => sig.lineType === line.type);

            if (signed || !(key in initialData)) return;

            initialData[key] = getCertLineDefaultDate({
              group: group as CertSignatureGroup,
              defaultDateLogic: line.defaultDateLogic,
              inspectionDOI: inspectionContext.inspection.created_date,
              timezone,
              requiredFindingCompletion,
              // SW2-2539: this inspection's client's locks, from the same cert state.
              lockedGroups: (query.data as any)?.lockedCertGroups,
              // `isCDOT` covers the whole CDOT family and keys off the INSPECTION's template, unlike the
              // `projectContext.template.name === 'CDOT'` check it replaces, which tested one name on the
              // PROJECT's default template.
              isCDOT: (query.data as any)?.isCDOT,
            });
          });
        },
      );

      return Object.keys(initialData).reduce((data, key) => {
        /** @important, use lodash.set, to ensure compatibility with context */
        set(data, key, initialData[key]);

        return data;
      }, {});
    }

    return {};
    /**
     * Dependencies must be VALUE-stable, not reference-stable. `FormContextProvider` re-writes every
     * field whenever `initialValue`'s identity changes, so depending on an object that is rebuilt each
     * render would clobber a date the signer had already edited.
     *
     * This list also fixes a real bug: the old `[query.data]` meant the compliance default was computed
     * before `findings` and the async backdating flag had arrived, and was never recomputed — so the
     * pre-fill was frequently wrong on first paint, which is the symptom this ticket is about.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query.data,
    timezone,
    requiredCompletionMs,
    inspectionContext.inspection.created_date,
  ]);

  const flashSuccessModal = () => {
    window.setTimeout(() => setShowModal(null), 2000);
    setShowModal('success');
  };

  const onSave: SaveHandler = useCallback(
    async ({ group, lineType, formData }) => {
      setShowModal('loading');

      const data: CreateApiParam = {
        inspectionId,
        group,
        lineType,
        date: get(
          formData,
          fieldKeyCodec.encode(lineType, CertSchemaField.date),
        ),
      };

      const getDefaultMsg = notificationSettings.filter((e: { name: any; }) => e.name === "Inspection Complete")[0].default_email_message

      if (group === SignatureGroupType.inspection) {
        data.emailMessage = get(formData, CertSchemaField.emailMessage);
        data.default_message = getDefaultMsg
      }

      return certificationApi
        .create(data)
        .then(
          // Sent: the server's answer (backend #479). Queued behind offline work (SW2-2459): no answer yet.
          async (result: { data?: CertificationCreateResponse | void; queued?: true } | undefined) => {
            // Work after a successful save is kept apart from the save: a failure here must not
            // be reported as "not saved" (and invite signing again).
            try {
              // lock inspection questions after compliance signature — queued or sent: an answer
              // changed after signing would be uploaded ahead of the queued signature (PR #156 review)
              if (data.lineType === SignatureGroupType.compliance) {
                inspectionContext.updateInspection(
                  Object.assign(inspectionContext.inspection, {
                    compliance_date: data.date,
                  }),
                );
              }

              // SW2-2459: queued behind the project's offline work; the sync sends it, and the
              // report goes out then — or, on a form that requires review, the review is raised
              // then and the report waits for it (SW2-2248).
              if (result?.queued) {
                setShowModal(null);
                toast.info(queuedSignatureMessage((query.data as any)?.review));
                query.refetch();

                return true;
              }

              flashSuccessModal();

              // SW2-2248: Inspection Complete is sent in the background, so the sign returns before
              // the email goes out. Tell the signer which way it went (backend #479).
              const outcome = result?.data || undefined;

              if (outcome?.reviewRaised) {
                toast.success('Signed. Sent for review; the report will be emailed once approved.');
              } else if (outcome?.distributed) {
                toast.success('Signed. The inspection report is being emailed to recipients.');
              }

              // SW2-2459: the user's own first inspection signature went through — offer to
              // remove a download that has nothing left to sync. Not when it raised a review
              // (SW2-2248): the reviewer can send the inspection back to be edited, so the download
              // is kept. The server says so (backend #479); before #479 its body is empty, so the
              // review state this page loaded before signing decides.
              if (
                !outcome?.reviewRaised &&
                !reviewHoldsReport((query.data as any)?.review) &&
                group === SignatureGroupType.inspection &&
                data.lineType === (query.data as any)?.signatureGroups?.inspection?.[0]?.type &&
                projectContext?.project?.id
              ) {
                offerDownloadRemoval(projectContext.project.id).catch((err: any) =>
                  // eslint-disable-next-line no-console
                  console.error('Offering to remove the download failed', err),
                );
              }

              inspectionsCertDue.refetch();
              fetchStatsFuncForID.refetch();
              query.refetch();
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error('Signature saved; refreshing the page afterwards failed', err);
            }

            return true;
          },
          (err: any) => {
            setShowModal(null);

            // SW2-2459: reported. The rejection used to go nowhere: no message, and the inspection
            // stayed read-only though nothing was signed. The caller restores its lock (it knows
            // the state before the attempt) when this resolves false.
            // eslint-disable-next-line no-console
            console.error('Saving the signature failed', err);
            toast.error(
              `The signature was not saved: ${
                typeof err === 'string'
                  ? err
                  : err?.response?.data?.message || err?.message || 'unknown error'
              }. Please try again.`,
            );

            return false;
          },
        );
    },
    [inspectionId, query.data, projectContext?.project?.id],
  );

  const reload = () => {
    query.refetch();
  }

  useEffect(
    () => () => {
      query.remove();
    },
    [],
  );

  if (query.isLoading || query.data === undefined) {
    return <Loading />;
  }

  return (
    <InspectionCertificationContext.Provider
      value={{
        // loading: query.isLoading,
        error: String(query.error) || undefined,
        state: query.data,
        onSave,
        flashSuccess: flashSuccessModal,
        reload
      }}
    >
      <FormContextProvider initialValue={initialFormData}>
        {children}
      </FormContextProvider>

      <SuccessNotification show={showModal === 'success'} />
      <LoadingModal show={showModal === 'loading'} />
    </InspectionCertificationContext.Provider>
  );
};

export default InspectionCertificationContext;
